import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import { authAPI, mfaAPI } from '../services/api';
import { trackLogout } from '../services/activityTracker';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isExpired: boolean;
  login: (username: string, password: string, isAdmin?: boolean) => Promise<boolean | { mfa_required: true; mfa_token: string }>;
  completeMfaLogin: (mfaToken: string, code: string) => Promise<boolean>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already authenticated on app load
    const validateAuthentication = async () => {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      
      if (token && userData) {
        try {
          const parsedUser = JSON.parse(userData);
          
          // Validate the token by making a profile request
          try {
            const profileResponse = await authAPI.getProfile();
            // Token is valid, merge profile data with login-specific fields preserved from localStorage
            const updatedUser: User = {
              ...parsedUser,
              ...profileResponse.data,
              role: parsedUser.role || 'customer',
              is_company_owner: profileResponse.data.is_company_owner ?? parsedUser.is_company_owner ?? false,
              is_company_admin: profileResponse.data.is_company_admin ?? parsedUser.is_company_admin ?? false,
              permissions: profileResponse.data.permissions ?? parsedUser.permissions ?? null,
              plan: profileResponse.data.plan || parsedUser.plan || null,
            };
            setUser(updatedUser);
            localStorage.setItem('user', JSON.stringify(updatedUser));
          } catch (error) {
            console.log('Token validation failed:', error);
            // Token is invalid, clear storage
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            setUser(null);
          }
        } catch (error) {
          console.error('Error parsing user data:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          setUser(null);
        }
      }
      setLoading(false);
    };

    validateAuthentication();
  }, []);

  const _processLoginResponse = async (response: any, isAdmin: boolean): Promise<boolean> => {
    if (response.data.access) {
      localStorage.setItem('token', response.data.access);
      localStorage.setItem('refreshToken', response.data.refresh);
      
      let profileData: any = null;
      try {
        const userResponse = await authAPI.getProfile();
        profileData = userResponse.data;
      } catch (profileError) {
        console.warn('Profile fetch failed after login, using login response data:', profileError);
      }

      const isCompany = response.data.user?.is_company || profileData?.is_company;
      const isCompanyAdmin = response.data.user?.is_company_admin || false;
      const backendRole = response.data.user?.role;
      const userData: User = {
        ...(profileData || {}),
        ...response.data.user,
        role: backendRole || (isAdmin ? 'admin' : ((isCompany || isCompanyAdmin) ? 'company' : 'customer')),
        subscription_tier: response.data.user?.subscription_tier || profileData?.subscription_tier,
        expiration_date: response.data.user?.expiration_date || profileData?.expiration_date,
        days_remaining: response.data.user?.days_remaining ?? profileData?.days_remaining,
        is_expired: response.data.user?.is_expired || profileData?.is_expired || false,
        is_company_owner: response.data.user?.is_company_owner || false,
        is_company_admin: response.data.user?.is_company_admin || false,
        has_custom_branding: response.data.user?.has_custom_branding || false,
        plan: response.data.user?.plan || null,
        customer_count: response.data.user?.customer_count ?? 0,
        customer_limit: response.data.user?.customer_limit ?? 0,
        permissions: response.data.user?.permissions || null,
      };
      
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      return true;
    }
    return false;
  };

  const login = async (username: string, password: string, isAdmin = false): Promise<boolean | { mfa_required: true; mfa_token: string }> => {
    try {
      setLoading(true);
      const response = await authAPI.login(username, password, isAdmin);
      
      // Check if MFA is required
      if (response.data.mfa_required) {
        return { mfa_required: true, mfa_token: response.data.mfa_token };
      }

      const result = await _processLoginResponse(response, isAdmin);
      return result;
    } catch (error: any) {
      console.error('Login error:', error);
      // Clear any tokens that might have been partially stored
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      // Re-throw if it's an unverified email error so callers can handle it
      if (error?.response?.data?.email_unverified) {
        throw error;
      }
      // Re-throw if it's a 403 with a specific error message (e.g. subdomain restriction)
      if (error?.response?.status === 403) {
        throw error;
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  const completeMfaLogin = async (mfaToken: string, code: string): Promise<boolean> => {
    try {
      setLoading(true);
      const response = await mfaAPI.verify(mfaToken, code);
      return await _processLoginResponse(response, false);
    } catch (error: any) {
      console.error('MFA verification error:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    trackLogout();
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setUser(null);
  };

  const refreshUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const profileResponse = await authAPI.getProfile();
      const currentUser = localStorage.getItem('user');
      const parsed = currentUser ? JSON.parse(currentUser) : {};
      const updatedUser: User = {
        ...parsed,
        ...profileResponse.data,
        role: parsed.role || 'customer',
        is_company_owner: profileResponse.data.is_company_owner ?? parsed.is_company_owner ?? false,
        is_company_admin: profileResponse.data.is_company_admin ?? parsed.is_company_admin ?? false,
        permissions: profileResponse.data.permissions ?? parsed.permissions ?? null,
        plan: profileResponse.data.plan || parsed.plan || null,
        subscription_tier: profileResponse.data.subscription_tier || parsed.subscription_tier,
        is_expired: profileResponse.data.is_expired ?? parsed.is_expired ?? false,
        days_remaining: profileResponse.data.days_remaining ?? parsed.days_remaining,
        expiration_date: profileResponse.data.expiration_date ?? parsed.expiration_date,
        customer_count: profileResponse.data.customer_count ?? parsed.customer_count ?? 0,
        customer_limit: profileResponse.data.customer_limit ?? parsed.customer_limit ?? 0,
        has_custom_branding: profileResponse.data.has_custom_branding ?? parsed.has_custom_branding ?? false,
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
  };

  const isExpired = !!(user?.is_expired);

  const value = {
    user,
    isAuthenticated: !!user,
    isExpired,
    login,
    completeMfaLogin,
    logout,
    refreshUser,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};